// Owned by mju-ai-workbench/shared/schemas/microapps.ts — the microapp manifest, the catalogue's listing and the policy clause (D354 phase 3). The AIOE session mirrors it to mju-aioe/packages/shared/src/schemas/microapps.ts; the workbench never mirrors it back. Keep the two identical.
/**
 * Microapps: a small saved UI bound to the Control API that lives on Home, in the dock, on the board or in a conversation
 * (D354, docs/plans/2026-10-home-and-microapps.md "B. Microapps" and "Microapps are shared through the catalogues").
 * OURS: the workbench defines these shapes; the hub implements `/apps/v1` from them, and the AIOE mirrors the file and builds
 * the same routes at the organisation.
 *
 * A microapp is data: a manifest (who made it, what it reads and may do, where it shows) and a `UiSpecV1`
 * (shared/schemas/uiSpec.ts), or, beside it, a **custom view** (`view: { kind: "custom" }`): HTML, CSS and JavaScript an agent
 * wrote, which the window runs only in a sandboxed frame that talks to the Workbench over a message bridge exposing exactly what
 * the manifest declares (webview-ui/src/components/apps/customViewBridge.ts). Nothing in a manifest runs in the Workbench itself,
 * and it never asks for more than its declared methods need.
 * What it may read is a binding (a Control API read with params), a tool read (a read-class tool from the MCP servers the
 * policy already allows agents) or a web read (a GET to an allowed origin) — the last two run on the host, with the DLP floor on
 * the answer, cached for the refresh interval the manifest states; what it may do is a declared action (a Control API write with
 * its scope), run as the person, audited with the app's id and version. The ceilings are in this file: the closed method tables,
 * the scopes no microapp is ever granted, and the policy clause.
 *
 * Where an app lives, and the routes at a project's home (the team's hub; the organisation for Enterprise). Base `/apps/v1`;
 * `:projectId` is the project (`prj_…`); the caller is a paired device or a signed-in person, as every hub route.
 *
 *   GET    /apps/v1/:projectId                         → {@link AppCatalogue}: the newest version of each app the caller may see
 *                                                         (listed ones; the caller's own and, for an owner or administrator,
 *                                                         pending ones too).
 *   GET    /apps/v1/:projectId/:appId                  → { versions: {@link AppListing}[] }: every version, newest first.
 *   PUT    /apps/v1/:projectId/:appId/:version         {@link AppPublishRequest} → 201 {@link AppListing}. A version is immutable:
 *                                                         409 `version_exists` for one already published; the version must be
 *                                                         newer than the newest. The home checks the manifest against the
 *                                                         `microapps` policy: 403 `publishing_off`, `scope_exceeded`,
 *                                                         `writes_not_allowed`, `app_denied`; `status` is `listed` under
 *                                                         `publishing: "anyone"` and `pending` under `"review"`.
 *   POST   /apps/v1/:projectId/:appId/:version/decide  {@link AppReviewDecision} → {@link AppListing}. An owner or administrator;
 *                                                         the author may not approve their own (403 `own_app`).
 *   DELETE /apps/v1/:projectId/:appId                  → { withdrawnAt }: the author or an administrator. Every version is marked
 *                                                         withdrawn; it stops being offered and stays installed where it is.
 *   POST   /apps/v1/:projectId/:appId/install          { version, hash } → { installs }: counted once per person per version.
 *
 * Refusals are `{ error, message }` with the words in {@link MICROAPP_ERRORS} for a person; a missing scope on a device is -32001.
 *
 * Words for people: app, Home, publish, review, withdraw, reads, can do. Never "manifest", "binding" or "scope" in the interface.
 */
import { z } from "zod";
import { APP_LABELS } from "../apps/appStatement.js";
import { originHost, originsOfReads, webOrigin } from "../apps/webOrigin.js";
import { pipelineRepositoriesProblem } from "../apps/repositoryNames.js";
import { actionsOf } from "../ui/uiSpecModel.js";
import { UI_TONES, uiSpecSchema, validateUiSpec } from "./uiSpec.js";
export const MICROAPP_SCHEMA_VERSION = 1;
/** The icons an app may wear: the Art set the app already draws with. The renderer maps each name to its Gravity icon. */
export const APP_ICONS = [
    "chart",
    "rocket",
    "list-check",
    "branches",
    "receipt",
    "clock",
    "bell",
    "book",
    "flask",
    "gear",
    "persons",
    "folder",
    "pulse",
    "tray",
    "route",
    "binoculars",
    "magnifier",
    "sparkles",
    "layout",
    "graph",
    "scales",
    "comment",
    "display",
    "house",
    "star",
    "calendar",
];
/** Where an app may show: a card in Home's feed, a tile on the feed-off board, a pin in the dock, a block in a conversation. */
export const APP_PLACEMENTS = ["feed", "board", "dock", "conversation"];
/** Where a stored or listed app came from, for the Apps page and the drawer. */
export const APP_WHERES = ["mine", "repository", "team", "organisation"];
export const APP_WHERE_WORDS = { mine: "Mine", repository: "This repository", team: "Your team", organisation: "Your organisation" };
// ── What an app may read and do ────────────────────────────────────────
/** The scopes an app may ask for (a subset of the Control API's). Everything else is {@link MICROAPP_EXCLUDED_SCOPES}. */
export const MICROAPP_SCOPES = ["agents:read", "tasks:read", "memory:read", "tasks:write", "prompts:write"];
/** What no microapp is ever granted, whatever its manifest says or the policy allows: approvals, settings, git, terminals, the window. */
export const MICROAPP_EXCLUDED_SCOPES = ["permissions:write", "settings:write", "git:write", "ui:view", "ui:control", "terminals:read", "terminals:write", "agents:write", "teams:write", "composer:write", "memory:write", "diagrams:write", "shell:read", "shell:write"];
/** The default ceiling (`microapps.maxScopes` absent): the read-only preset, as an observer client has. */
export const MICROAPP_DEFAULT_MAX_SCOPES = ["agents:read", "tasks:read"];
/** A scope in the words a person reads at install ("Reads your tasks"). */
export const MICROAPP_SCOPE_WORDS = {
    "agents:read": "your agents and runs",
    "tasks:read": "your tasks, plans, roadmap, workflows and routines",
    "memory:read": "the project's memory",
    "tasks:write": "the task board, and starting, cancelling or running workflows and routines",
    "prompts:write": "a message to an agent",
};
/** Control API reads a binding may name, with the scope each needs. A closed table: a method not here is refused. */
export const MICROAPP_READ_METHODS = {
    "workbench.status": "agents:read",
    "sessions.list": "agents:read",
    "agents.activity": "agents:read",
    "teams.list": "agents:read",
    "runs.list": "agents:read",
    "usage.history": "agents:read",
    "commits.timeline": "agents:read",
    "pullRequests.list": "agents:read",
    "pipelines.list": "agents:read",
    "board.list": "tasks:read",
    "plans.list": "tasks:read",
    "plans.get": "tasks:read",
    "roadmap.get": "tasks:read",
    "decisions.list": "tasks:read",
    "workflows.list": "tasks:read",
    "workflows.runs": "tasks:read",
    "routines.list": "tasks:read",
    "routines.activity": "tasks:read",
    "memory.status": "memory:read",
};
/** Control API writes an action may name, with the scope each needs. Never approvals, settings, git, terminals or the window. */
export const MICROAPP_WRITE_METHODS = {
    "board.create": "tasks:write",
    "board.update": "tasks:write",
    "board.move": "tasks:write",
    "board.assign": "tasks:write",
    "board.comment": "tasks:write",
    "roadmap.upsert": "tasks:write",
    "workflows.start": "tasks:write",
    "workflows.cancel": "tasks:write",
    "workflows.rerun": "tasks:write",
    "routines.runNow": "tasks:write",
    "routines.setEnabled": "tasks:write",
    "facilitator.send": "prompts:write",
};
/** Reads of what a project holds back by label or classification: a shared app does not read them where the device's floor is confidential or above (the DLP gate). */
export const MICROAPP_SENSITIVE_METHODS = ["memory.status"];
/** A refusal's word, and the sentence a person is shown. */
export const MICROAPP_ERRORS = {
    microapp_off: "Apps are switched off here.",
    taken: "Someone else already shares an app with that name. Give yours another name.",
    not_reviewer: "Only an owner of the project can review this.",
    invalid: "This was not sent the way the catalogue needs it.",
    scope_exceeded: "This app asks for more than your organisation lets an app have.",
    scope_narrower: "This app reads or does more than it says it needs.",
    scope_excluded: "This app asks for something an app is never allowed to do.",
    action_undeclared: "That is not something this app said it could do.",
    binding_undeclared: "That is not something this app said it reads.",
    binding_too_large: "What this app asked for is too large to show.",
    method_not_allowed: "This app names something an app cannot use.",
    writes_not_allowed: "Your organisation does not let apps change anything.",
    app_denied: "Your organisation does not allow this app.",
    not_listed: "Your organisation lets only listed apps run here.",
    dlp_floor: "This app reads something your data rules keep out of shared apps.",
    publishing_off: "Your organisation does not let people publish apps.",
    version_exists: "That version is already published. Publish a newer one.",
    own_app: "You cannot approve your own app.",
    changed: "This app changed since you added it. Look at what it can do, then add it again.",
    custom_views_off: "Your organisation does not let apps have custom views.",
    tool_not_read: "An app may use only a tool that reads, and only one that is allowed without asking first.",
    tool_unavailable: "That tool is not one your agents may use here.",
    web_url_bad: "A web read needs a plain https address (http only for this computer), with no sign-in in it.",
    origin_not_allowed: "Your organisation's network policy does not let apps read from that address.",
    origin_not_agreed: "This app reads from a web address that has not been agreed yet.",
    web_failed: "That web address did not answer.",
    // D390: a web read may POST fixed JSON (a GraphQL query) and use a key the person keeps in Settings › Secrets.
    web_body_bad: "A web read's body is fixed JSON, and a body is sent only with POST.",
    key_not_agreed: "This app uses a key with a web address that has not been agreed yet.",
    view_unsafe: "A custom view cannot do that: it is drawn in a sandbox and reads only through the app's own reads.",
    // D364: governance of what an app reads from and sends outside the Workbench. Each is a sentence on the app's card, never a dialog.
    binding_into_external: "A read from outside the Workbench may use only fixed values, never what another read returned.",
    external_off: "Your organisation does not let apps read from outside the Workbench.",
    external_unapproved: "Reading from outside the Workbench needs an owner or administrator to approve this app.",
    connector_not_allowed: "Your organisation does not let apps use this connector.",
    label_above: "Your organisation keeps data at this label out of apps.",
    egress_off: "Your organisation does not let apps send data out of the Workbench.",
    egress_unapproved: "Sending data out of the Workbench needs an owner or administrator to approve this app.",
    egress_dlp: "What this would send is labelled above what may leave the Workbench, so nothing was sent.",
    approval_revoked: "Your organisation withdrew its approval of what this app reads from or sends outside the Workbench.",
    above_clearance: "This app shows data labelled above what can be shown here.",
    consent_needed: "This app changed what it reads or sends. Allow it on your computer before it runs.",
};
// ── Custom views, tool reads and web reads ─────────────────────────────
/** How large a custom view's source may be: a screen, not an application. */
export const CUSTOM_VIEW_LIMITS = { html: 40_000, css: 20_000, js: 60_000 };
/** What a tool read or a web read refreshes at when the manifest says nothing, and the least and most it may say. */
export const SOURCE_REFRESH = { default: 300, min: 30, max: 86_400 };
/** How many sources an app may have beside its Control API reads. */
export const SOURCE_LIMITS = { toolReads: 4, webReads: 4, origins: 8 };
// ── The manifest ───────────────────────────────────────────────────────
const id = z.string().regex(/^[a-z0-9][a-z0-9-]{0,40}$/, "an app id is lower-case letters, digits and hyphens");
const nameOf = z.string().regex(/^[a-z][a-z0-9_-]{0,39}$/, "a name is lower-case letters, digits, hyphens and underscores");
const paramValue = z.union([z.string().max(200), z.number(), z.boolean()]);
/** D473: an action's params may also hold `inputs`, a workflow's named answers; {@link manifestProblems} says which method may. */
const params = z.record(z.string().max(40), z.union([paramValue, z.record(z.string().max(60), paramValue)]));
/** A read's params may also hold a short list of names (D390: `pipelines.list { repositories }`); {@link manifestProblems} says which. */
const bindingParams = z.record(z.string().max(40), z.union([paramValue, z.array(z.string().max(200)).max(16)]));
const refresh = z.number().int().min(SOURCE_REFRESH.min).max(SOURCE_REFRESH.max).optional();
export const microappBindingSchema = z
    .object({
    name: nameOf,
    method: z.string().max(60),
    params: bindingParams.optional(),
    /** Re-read this often; never below 15 s, and paused while the window is hidden. */
    refreshSeconds: z.number().int().min(15).max(3600).optional(),
})
    .strict();
/** A read-class tool from an MCP server the policy lets agents use. Runs on the host; the answer is cached for `refreshSeconds`. */
export const microappToolReadSchema = z
    .object({
    name: nameOf,
    /** The tool server's name as the workbench lists it (Settings, Connectors). */
    server: z.string().min(1).max(64),
    tool: z.string().min(1).max(80),
    args: params.optional(),
    refreshSeconds: refresh,
})
    .strict();
/** The longest fixed body a web read may POST (a GraphQL query fits). */
export const WEB_READ_BODY_MAX = 8_000;
/**
 * D390: a key the person keeps in Settings › Secrets, put in on the host as `Authorization: Bearer …` and sent only to this read's
 * own address, which must be one the person agreed to. Never in the app's page, its statement, the audit or the log: only its name.
 * `service` is how the person knows it ("Expo"), for the sentence that says where to add it.
 */
export const microappWebAuthSchema = z
    .object({
    secret: z.string().regex(/^[A-Za-z][A-Za-z0-9_.-]{0,99}$/, "a key's name starts with a letter and holds letters, digits, _ . -"),
    scheme: z.literal("bearer"),
    service: z.string().min(1).max(40).optional(),
})
    .strict();
/**
 * A GET (or, D390, a POST of fixed JSON) to one allowed origin. Runs on the host; no redirect to another origin is followed, and a
 * POST follows none; the answer is cached for `refreshSeconds`.
 */
export const microappWebReadSchema = z
    .object({
    name: nameOf,
    url: z.string().min(8).max(500),
    method: z.enum(["GET", "POST"]).optional(),
    /** POST only: fixed JSON (a GraphQL query and its variables). Nothing another read returned may go into it. */
    body: z.string().min(2).max(WEB_READ_BODY_MAX).optional(),
    auth: microappWebAuthSchema.optional(),
    refreshSeconds: refresh,
})
    .strict();
/** What a screen is made of. `blocks` (or no `view`): the manifest's `ui`. `custom`: HTML, CSS and JavaScript, run only in the sandboxed frame. */
export const microappViewSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("blocks") }).strict(),
    z
        .object({
        kind: z.literal("custom"),
        /** The body of the page: markup only. The view's own script talks to the Workbench through the `app` object the frame provides. */
        html: z.string().min(1).max(CUSTOM_VIEW_LIMITS.html),
        css: z.string().max(CUSTOM_VIEW_LIMITS.css).optional(),
        js: z.string().max(CUSTOM_VIEW_LIMITS.js).optional(),
    })
        .strict(),
]);
export const microappActionSchema = z
    .object({
    name: nameOf,
    method: z.string().max(60),
    /** The scope the method needs: stated so a reader sees it, and checked against the method. */
    scope: z.string().max(40),
    /** Fixed params; a string may carry `{{values.field}}` (the block's fields) or `{{row.key}}` (a table row), filled when it is pressed. */
    params: params.default({}),
    /** A sentence the person confirms before it runs ("Mark this task done?"). Writes without one run on the press alone. */
    confirm: z.string().max(160).optional(),
})
    .strict();
const author = z.object({ id: z.string().min(1).max(200), name: z.string().min(1).max(120) }).strict();
export const microappManifestSchema = z
    .object({
    schema: z.literal(MICROAPP_SCHEMA_VERSION),
    id,
    name: z.string().min(1).max(40),
    purpose: z.string().min(1).max(160),
    icon: z.enum(APP_ICONS),
    tone: z.enum(UI_TONES),
    /** `major.minor.patch`; a published version is immutable. */
    version: z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/),
    author,
    /** The read scopes it needs: must cover its bindings (checked by {@link manifestProblems}). Write scopes are on each action. */
    scopes: z.array(z.string().max(40)).max(8),
    bindings: z.array(microappBindingSchema).max(8).default([]),
    /** Read-class tools from the servers the policy lets agents use. */
    toolReads: z.array(microappToolReadSchema).max(SOURCE_LIMITS.toolReads).optional(),
    /** GETs to allowed origins. */
    webReads: z.array(microappWebReadSchema).max(SOURCE_LIMITS.webReads).optional(),
    /**
     * The origins its web reads may go to, as the person agreed them when they saved it (or as the repository commits them): with
     * no `network` clause in the policy a web read goes only to an origin listed here. The workbench fills it; a draft does not.
     */
    origins: z.array(z.string().max(200)).max(SOURCE_LIMITS.origins).optional(),
    actions: z.array(microappActionSchema).max(8).default([]),
    /** Absent or `blocks`: `ui` is the screen. `custom`: the sandboxed view is, and `ui` is what shows where a frame cannot (a conversation, a policy that turns custom views off). */
    view: microappViewSchema.optional(),
    ui: uiSpecSchema,
    placement: z.array(z.enum(APP_PLACEMENTS)).min(1).max(4),
    /**
     * D364: the label an owner set for the app, when it is higher than what its reads return (the effective label is the higher
     * of the two). Absent: the highest label of its reads.
     */
    label: z.enum(APP_LABELS).optional(),
    /** SHA-256 of the canonical manifest without `hash`, `publishedAt` and `withdrawnAt` ({@link canonicalManifest}). */
    hash: z.string().regex(/^[0-9a-f]{64}$/).optional(),
    publishedAt: z.string().max(40).optional(),
    withdrawnAt: z.string().max(40).optional(),
})
    .strict();
/** What an agent or the builder proposes: the author and the hash are the workbench's to fill. */
export const microappDraftSchema = microappManifestSchema.omit({ hash: true, publishedAt: true, withdrawnAt: true, author: true, schema: true, version: true, origins: true }).extend({ version: microappManifestSchema.shape.version.optional() });
/** The manifest with the fields a hash must not cover removed, keys sorted: what is hashed and what is compared. */
export function canonicalManifest(m) {
    const { hash: _h, publishedAt: _p, withdrawnAt: _w, ...rest } = m;
    const sort = (v) => {
        if (Array.isArray(v))
            return v.map(sort);
        if (v && typeof v === "object")
            return Object.fromEntries(Object.entries(v).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([k, x]) => [k, sort(x)]));
        return v;
    };
    return JSON.stringify(sort(rest));
}
/** The tool reads and web reads of a manifest (absent means none: a manifest from before they existed hashes as it did). */
export const toolReadsOf = (m) => m.toolReads ?? [];
export const webReadsOf = (m) => m.webReads ?? [];
/** Every name a screen may read by: Control API reads, tool reads and web reads. */
export const readNamesOf = (m) => [...m.bindings.map((b) => b.name), ...toolReadsOf(m).map((t) => t.name), ...webReadsOf(m).map((w) => w.name)];
/** Whether a manifest's screen is a custom view. */
export const isCustomView = (m) => m.view?.kind === "custom";
export { originHost, webOrigin };
/** The distinct origins a manifest's web reads go to. */
export const originsOf = (m) => originsOfReads(webReadsOf(m));
/**
 * What a custom view's source may not contain. This is guidance for the drafting agent, so it is told at once what cannot work;
 * the boundary is the sandbox (no network, no same-origin, a strict CSP, a message bridge that answers only what the manifest
 * declares), which holds whatever a view does. Sentences for a person, with the offending word; empty when fine.
 */
export function customViewProblems(view) {
    const out = [];
    const say = (what) => out.push(`${MICROAPP_ERRORS.view_unsafe} (${what})`);
    const html = view.html;
    const tag = /<\s*(iframe|frame|frameset|object|embed|base|meta|link|form|portal|script\b[^>]*\bsrc)\b/i.exec(html);
    if (tag)
        say(`the <${tag[1].replace(/\s.*$/, "")}> element`);
    if (/<\s*script\b/i.test(html) && !tag)
        say("a <script> in the markup: put the script in js");
    const external = /(?:\b(?:src|href|action|poster|srcset|data|formaction)\s*=\s*["']?\s*(?:https?:)?\/\/|url\(\s*["']?\s*(?:https?:)?\/\/|@import\b)/i;
    if (external.test(html) || external.test(view.css ?? ""))
        say("an address on the web: a view loads nothing from the web, it reads through the app");
    const js = view.js ?? "";
    const call = /\b(fetch|XMLHttpRequest|WebSocket|EventSource|sendBeacon|importScripts|RTCPeerConnection)\b|\bimport\s*\(|\bwindow\.open\b|\blocation\s*(?:\.href\s*)?=[^=]|\bdocument\.cookie\b/.exec(js);
    if (call)
        say(call[0].trim());
    return out;
}
/**
 * A draft that carries a custom view needs no `ui` of its own: this fills the plain sentence that shows where a frame cannot be
 * drawn. Applied before a draft is parsed; anything else comes back as it was.
 */
export function withFallbackUi(raw) {
    if (!raw || typeof raw !== "object")
        return raw;
    const d = raw;
    const view = d.view;
    if (d.ui !== undefined || !view || view.kind !== "custom")
        return raw;
    const purpose = typeof d.purpose === "string" && d.purpose ? d.purpose : typeof d.name === "string" ? d.name : "An app";
    return { ...d, ui: { v: 1, title: typeof d.name === "string" ? d.name : "App", alt: purpose, root: { type: "text", text: purpose } } };
}
/** Every scope the app uses: its read scopes and its actions' scopes. */
export function appScopes(m) {
    return [...new Set([...m.scopes, ...m.actions.map((a) => a.scope)])].sort();
}
/**
 * Problems a manifest has on its own, whatever the policy: unknown or excluded methods and scopes, a scope that does not match
 * its method, bindings its `scopes` do not cover, actions and bindings that share a name, and a `run` in the UI naming
 * something undeclared. Sentences for a person; empty when sound.
 */
export function manifestProblems(m) {
    const out = [];
    const excluded = new Set(MICROAPP_EXCLUDED_SCOPES);
    const reads = MICROAPP_READ_METHODS;
    const writes = MICROAPP_WRITE_METHODS;
    for (const s of appScopes(m)) {
        if (excluded.has(s))
            out.push(`${MICROAPP_ERRORS.scope_excluded} (${s})`);
        else if (!MICROAPP_SCOPES.includes(s))
            out.push(`${MICROAPP_ERRORS.method_not_allowed} (the scope ${s})`);
    }
    for (const b of m.bindings) {
        const need = reads[b.method];
        if (!need)
            out.push(`${MICROAPP_ERRORS.method_not_allowed} (it reads ${b.method})`);
        else if (!m.scopes.includes(need))
            out.push(`${MICROAPP_ERRORS.scope_narrower} (${b.name} needs ${need})`);
        out.push(...bindingParamProblems(b));
    }
    for (const a of m.actions) {
        const need = writes[a.method];
        if (!need)
            out.push(`${MICROAPP_ERRORS.method_not_allowed} (it does ${a.method})`);
        else if (a.scope !== need)
            out.push(`${MICROAPP_ERRORS.scope_narrower} (${a.name} needs ${need}, not ${a.scope})`);
        out.push(...bindingParamProblems(a));
    }
    for (const w of webReadsOf(m)) {
        if (!webOrigin(w.url))
            out.push(`${MICROAPP_ERRORS.web_url_bad} (${w.name})`);
        if (w.body !== undefined && (w.method ?? "GET") !== "POST")
            out.push(`${MICROAPP_ERRORS.web_body_bad} (${w.name}: its body is sent only with POST)`);
        else if (w.body !== undefined && !isJson(w.body))
            out.push(`${MICROAPP_ERRORS.web_body_bad} (${w.name}: its body is not fixed JSON)`);
    }
    for (const name of externalReadsWithBindings(m))
        out.push(`${MICROAPP_ERRORS.binding_into_external} (${name})`);
    if (m.view?.kind === "custom")
        out.push(...customViewProblems(m.view));
    const names = [...m.bindings.map((b) => b.name), ...toolReadsOf(m).map((t) => t.name), ...webReadsOf(m).map((w) => w.name), ...m.actions.map((a) => a.name)];
    const dup = names.find((n, i) => names.indexOf(n) !== i);
    if (dup)
        out.push(`two things in this app are named "${dup}"`);
    for (const a of actionsOf(m.ui))
        if (a.do === "run" && !names.includes(a.name))
            out.push(`the screen runs "${a.name}", which the app does not declare`);
    return out;
}
/** A template marker: what would carry another read's value (or a press's) into a string. */
const TEMPLATE = /\{\{|\}\}|\$\{/;
/** In a JSON body only an opening marker counts: `}}` closes two objects there. */
const TEMPLATE_OPEN = /\{\{|\$\{/;
const isJson = (text) => {
    try {
        JSON.parse(text);
        return true;
    }
    catch {
        return false;
    }
};
/**
 * D390, the param rules of a read: a list is taken only by `pipelines.list`, as `repositories` (at most five names, each
 * `owner/repo` or `organisation/project/repo`); everything else is a scalar. D473: `workflows.start` also takes `inputs`, an
 * object of scalars (or `{{values.x}}` strings). Sentences; empty when sound.
 */
/** `workflows.start`'s `inputs`: at most twenty named values, each a string, number or true/false. */
function workflowInputsProblem(value) {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return "inputs are the workflow's named answers, as an object.";
    const entries = Object.entries(value);
    if (entries.length > 20)
        return "a workflow takes at most twenty inputs.";
    for (const [k, v] of entries) {
        if (!/^[A-Za-z][A-Za-z0-9_]{0,59}$/.test(k))
            return `${k.slice(0, 40)} is not a name an input can have.`;
        if (typeof v !== "string" && typeof v !== "number" && typeof v !== "boolean")
            return `the input ${k} is a single value, not a list or an object.`;
    }
    return undefined;
}
export function bindingParamProblems(b) {
    const out = [];
    for (const [key, value] of Object.entries(b.params ?? {})) {
        if (b.method === "pipelines.list" && key === "repositories") {
            const problem = pipelineRepositoriesProblem(value);
            if (problem)
                out.push(`${b.name}: ${problem}`);
        }
        else if (b.method === "workflows.start" && key === "inputs") {
            const problem = workflowInputsProblem(value);
            if (problem)
                out.push(`${b.name}: ${problem}`);
        }
        else if (Array.isArray(value) || key === "repositories")
            out.push(`${b.name}: a list of repositories is taken only by pipelines.list, as repositories.`);
        else if (value && typeof value === "object")
            out.push(`${b.name}: ${key} is a single value; only workflows.start takes a set of named answers, as inputs.`);
    }
    return out;
}
/**
 * D364, no exfiltration by construction: the names of the tool reads and web reads whose arguments or address carry a template.
 * An external read is made with literals only, so nothing another read returned can ride out in its query; refused on save and
 * again at run time.
 */
export function externalReadsWithBindings(m) {
    const out = [];
    for (const t of toolReadsOf(m))
        if (Object.entries(t.args ?? {}).some(([k, v]) => TEMPLATE.test(k) || (typeof v === "string" && TEMPLATE.test(v))))
            out.push(t.name);
    for (const w of webReadsOf(m)) {
        if (w.body !== undefined && TEMPLATE_OPEN.test(w.body)) {
            out.push(w.name);
            continue;
        }
        let raw = w.url;
        try {
            raw = decodeURIComponent(w.url);
        }
        catch {
            /* keep it as written */
        }
        if (TEMPLATE.test(w.url) || TEMPLATE.test(raw))
            out.push(w.name);
    }
    return out;
}
/** Parse and check a manifest: shape, the UI spec's own rules (a microapp may `run`), and {@link manifestProblems}. */
export function parseManifest(raw) {
    const parsed = microappManifestSchema.safeParse(raw);
    if (!parsed.success)
        return { ok: false, errors: parsed.error.issues.slice(0, 8).map((i) => `${i.path.join(".") || "app"}: ${i.message}`) };
    const spec = validateUiSpec(parsed.data.ui, { allowRun: true });
    if (!spec.ok)
        return { ok: false, errors: spec.errors.map((e) => `screen: ${e}`) };
    const manifest = { ...parsed.data, ui: spec.spec };
    const problems = manifestProblems(manifest);
    return problems.length ? { ok: false, errors: problems.slice(0, 8) } : { ok: true, manifest };
}
// ── Storage and the catalogue ──────────────────────────────────────────
/** An app as kept on this machine: its manifest, and where an installed copy came from. */
export const storedAppSchema = z
    .object({
    manifest: microappManifestSchema,
    savedAt: z.string().max(40),
    installedFrom: z.object({ where: z.enum(["team", "organisation"]), version: z.string().max(20), hash: z.string().max(64), at: z.string().max(40) }).strict().optional(),
})
    .strict();
export const APP_LISTING_STATUSES = ["listed", "pending", "rejected", "withdrawn"];
export const appPublishRequestSchema = z.object({ manifest: microappManifestSchema }).strict();
// ── The data-access statement and consent (D364) ───────────────────────
const labelEnum = z.enum(APP_LABELS);
const personRef = z.object({ id: z.string().max(200), name: z.string().max(120) }).strict();
/**
 * What an app touches, as one statement (shared/apps/appStatement.ts derives it; this is the shape AIOE mirrors and stores):
 * the Control API reads with the highest label each can return, the external sources by name, the actions and whether they send
 * data out, the effective label, and whether it puts data above Internal beside external data.
 */
export const appStatementSchema = z
    .object({
    v: z.literal(1),
    reads: z.array(z.object({ method: z.string().max(60), words: z.string().max(200), label: labelEnum }).strict()).max(16),
    external: z
        .array(z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("connector"), server: z.string().max(64), tool: z.string().max(80), words: z.string().max(200) }).strict(),
        // D390: `secret`, the name (never the value) of the key the person keeps in Settings › Secrets that goes with it.
        z.object({ kind: z.literal("web"), origin: z.string().max(200), words: z.string().max(240), secret: z.string().max(100).optional() }).strict(),
    ]))
        .max(16),
    actions: z.array(z.object({ name: z.string().max(40), method: z.string().max(60), words: z.string().max(120), sendsOut: z.string().max(80).optional() }).strict()).max(16),
    label: labelEnum,
    sendsOut: z.boolean(),
    combines: z.boolean(),
})
    .strict();
/** Who gave a consent: the person, or a reviewer on behalf of the team or the organisation (pre-consent through the catalogue). */
export const APP_CONSENT_KINDS = ["person", "team", "organisation"];
/**
 * A consent, kept with the app's version (D364): saving or adding an app is the person's consent to its statement; an approved
 * catalogue version carries the reviewer's. Kept on the person's machine (one per app, the newest) and in the audit as
 * `microapp.consent`. `digest` is SHA-256 of the canonical statement (`canonicalStatement`). `approver`: whoever gave it may
 * approve statements here (an owner or administrator, or the one person on a workbench no organisation governs), which is what
 * `external: "approved-only"` and `egress: "approved-only"` ask for. `withdrawnAt`: the organisation revoked its approval.
 */
export const appConsentSchema = z
    .object({
    v: z.literal(1),
    appId: z.string().max(60),
    version: z.string().max(20),
    hash: z.string().max(64),
    statement: appStatementSchema,
    digest: z.string().max(64),
    kind: z.enum(APP_CONSENT_KINDS),
    by: personRef,
    at: z.string().max(40),
    approver: z.boolean(),
    withdrawnAt: z.string().max(40).optional(),
    withdrawnBecause: z.string().max(300).optional(),
})
    .strict();
/** The reviewer's consent on a listing (D364): what they approved, who, when; `revokedAt` when an administrator withdrew it. */
export const appReviewConsentSchema = z.object({ statement: appStatementSchema, by: personRef, at: z.string().max(40), revokedAt: z.string().max(40).optional(), revokedBy: personRef.optional() }).strict();
/**
 * A decision on a pending version. An approval may carry the statement the reviewer saw (with the labels of their project): it
 * becomes the consent the listing keeps, once the home has checked it is no narrower than what the manifest itself says. Absent
 * (the Inbox's Approve), the home derives it from the manifest.
 */
export const appReviewDecisionSchema = z.object({ decision: z.enum(["approve", "reject"]), reason: z.string().max(400).optional(), statement: appStatementSchema.optional() }).strict();
/**
 * D364, the organisation's audit of an app: the rows the console shows per app are the workbench's audit actions
 * `microapp.consent` (a person's or a reviewer's), `microapp.consent.withdrawn` (the organisation revoked it), `microapp.refused`
 * (a policy stop, once per app version and what was stopped) and `microapp.egress` (each action that sent data out, or that the
 * data rules stopped) — never one per read. Each carries `detail.app`, `.version`, `.hash` and, for a consent, `.digest`.
 */
export const APP_GOVERNANCE_AUDIT_ACTIONS = ["microapp.consent", "microapp.consent.withdrawn", "microapp.refused", "microapp.egress"];
/**
 * The Inbox record an owner or administrator decides a pending version from (D316-style): addressed to the project's owners, kind
 * `decision`, needs-you, dedupe key {@link appReviewDedupeKey} (the Inbox lane rule counts the `app-review:` prefix). Its actions
 * are Approve (this rpc, `decision: "approve"`) and Review (`keepOpen`: opens the project's Apps page, where a rejection is given
 * with its reason). The record settles when the version is decided.
 */
export const APP_REVIEW_DECIDE_RPC = "aioe.apps.review.decide";
export const appReviewDedupeKey = (appId, version) => `app-review:${appId}@${version}`;
// ── The policy clause ──────────────────────────────────────────────────
/**
 * The `microapps` clause (workspace policy, `.mojoup/policy.json`; D354; `instruments`, a name it had for a day and that never
 * shipped, is read as the same clause). Left out: people build and add apps, an agent may only propose one; they read what
 * the read-only preset reads and cannot change anything; anyone may publish to their team; blocks in conversations are on.
 */
export const microappsPolicySchema = z.object({
    enabled: z
        .enum(["people-only", "anyone", "off"])
        .optional()
        .describe("Who may make an app. people-only (the default): a person builds or adds apps and an agent may only propose one for them to save. anyone: an agent may save one too. off: no apps; the dock pins and Home cards go, and blocks in conversations still follow blocks."),
    blocks: z.boolean().optional().describe("Whether agents may show blocks (forms, choices, tables) in conversations. Absent: true."),
    maxScopes: z.array(z.string()).optional().describe("The most an app may be granted, as Control API scopes. Absent: agents:read and tasks:read. Approvals, settings, git, terminals and the window are never granted."),
    allowWrites: z.boolean().optional().describe("Whether an app may declare actions that change something (create or move a task, comment). Absent: false."),
    publishing: z.enum(["anyone", "review", "off"]).optional().describe("What happens when a person publishes an app to the team or organisation: anyone (listed at once), review (an owner or administrator approves it first), off. Absent: anyone."),
    allow: z.array(z.string()).optional().describe("App ids that may run; when present, only these. Absent: any app that passes the other rules."),
    deny: z.array(z.string()).optional().describe("App ids that never run here."),
    customViews: z.enum(["on", "off"]).optional().describe("Whether an app may have a custom view: a screen an agent wrote in HTML, CSS and JavaScript, which runs only in a sandboxed frame that reads through the app's own declared reads. Absent: on. off: the catalogue does not list one, one already saved does not run, and the drafting agent is told to use blocks only."),
    mapTiles: z
        .string()
        .max(500)
        .refine((v) => v === "off" || /^https:\/\/[^\s/]+\S*$/.test(v) || /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?\S*$/.test(v), "off, or the address of a MapLibre style (https)")
        .optional()
        .describe("Where an app's map gets its tiles: the address of a MapLibre style, which pins it for everyone under this policy, or off, which turns the tiles off so a map draws its places as a list. Absent: the person's own setting (apps.mapTiles; OpenFreeMap's free style by default). The Workbench fetches the tiles, never the app; the tile provider sees which area is being viewed."),
    external: z
        .enum(["allow", "approved-only", "off"])
        .optional()
        .describe("Whether an app may read from outside the Workbench (a connector's tool, a web address). approved-only (the default): only in apps whose data-access statement an owner or administrator approved (the catalogue's review, or the one person on a workbench no organisation governs). allow: any app the person agreed to. off: none. What is stopped is said on the app in one line; the rest of the app still shows."),
    connectors: z.array(z.string().max(64)).max(100).optional().describe("Connector (tool server) names an app may read through. Absent or empty: whatever the tools clause already lets agents use without asking."),
    origins: z.array(z.string().max(200)).max(200).optional().describe("Web addresses an app may read from, as origins or host patterns (https://api.example.com, *.example.com). Absent or empty: whatever the network clause allows (or, with none, what the person agreed to)."),
    maxLabel: z.enum(APP_LABELS).optional().describe("The highest sensitivity label an app may read: public, internal, confidential or restricted. Absent: the project's classification. A read above it is stopped and the app shows the rest."),
    egress: z
        .enum(["off", "approved-only", "allow"])
        .optional()
        .describe("Whether an app's action may send data out of the Workbench (a task written to a board that mirrors to GitHub Issues, Azure Boards or Linear). off (the default): never. approved-only: only in apps an owner or administrator approved. allow: any app the person agreed to. Whatever this says, the data rules scan what would be sent and stop anything labelled above what may leave."),
    revoked: z.array(z.string().max(80)).max(500).optional().describe("Apps, by id or id@version, whose approval the organisation withdrew: the workbench withdraws the consent on its next policy pull, the app stops reading from and sending outside the Workbench, and each person who has it is told once in the Inbox."),
});
/** The clause with its defaults filled in. */
export function effectiveMicroappsPolicy(p) {
    const excluded = new Set(MICROAPP_EXCLUDED_SCOPES);
    return {
        enabled: p?.enabled ?? "people-only",
        blocks: p?.blocks ?? true,
        // The ceiling never includes what no app may have, whatever a file says.
        maxScopes: (p?.maxScopes ?? MICROAPP_DEFAULT_MAX_SCOPES).filter((s) => !excluded.has(s)),
        allowWrites: p?.allowWrites ?? false,
        publishing: p?.publishing ?? "anyone",
        ...(p?.allow ? { allow: p.allow } : {}),
        deny: p?.deny ?? [],
        customViews: p?.customViews ?? "on",
        ...(p?.mapTiles ? { mapTiles: p.mapTiles } : {}),
        external: p?.external ?? "approved-only",
        connectors: p?.connectors ?? [],
        origins: p?.origins ?? [],
        ...(p?.maxLabel ? { maxLabel: p.maxLabel } : {}),
        egress: p?.egress ?? "off",
        revoked: p?.revoked ?? [],
    };
}
const EXTERNAL_WORDS = { allow: "apps may read from outside the Workbench", "approved-only": "only approved apps read from outside the Workbench", off: "apps read nothing from outside the Workbench" };
const EGRESS_WORDS = { allow: "apps may send data out", "approved-only": "only approved apps send data out", off: "apps send nothing out" };
/** The clause in a sentence for the Workspace policy card. */
export function describeMicroappsPolicy(p) {
    const e = effectiveMicroappsPolicy(p);
    if (e.enabled === "off")
        return `Apps are off${e.blocks ? "; blocks in conversations are on" : "; so are blocks in conversations"}`;
    const who = e.enabled === "anyone" ? "an agent may save an app" : "an agent may only propose an app for a person to save";
    const reach = e.maxScopes.length ? `apps may read up to ${e.maxScopes.join(", ")}` : "apps may read nothing";
    const act = e.allowWrites ? "and may change the board" : "and cannot change anything";
    const pub = e.publishing === "off" ? "publishing is off" : e.publishing === "review" ? "published apps wait for an owner's review" : "published apps are listed at once";
    return `Apps: ${who}; ${reach} ${act}; ${pub}${e.allow ? `; only ${e.allow.length} listed app${e.allow.length === 1 ? "" : "s"} may run` : ""}${e.deny.length ? `; ${e.deny.length} blocked` : ""}${e.customViews === "off" ? "; custom views are off" : ""}; ${EXTERNAL_WORDS[e.external ?? "approved-only"]}; ${EGRESS_WORDS[e.egress ?? "off"]}${e.maxLabel ? `; they read nothing above ${e.maxLabel}` : ""}`;
}
//# sourceMappingURL=microapps.js.map